import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {withStyles} from '@material-ui/core/styles';
import {IconButton, Paper, Tooltip, Typography} from '@material-ui/core';
import {BackButton, localization} from '../../sc-utilities';
import MaterialTable from 'material-table';
import {Refresh} from '@material-ui/icons';
import * as action from "../../np-core/Auth/store/authActions";
import {fetchSupportedClientTypeUsage} from "./store/clientVersionsCheckActions";
import {fetchClientTypes} from "../../sc-dashboard/UserActivity/store/userActivityActions";
import DataRange from '../../sc-utilities/DateRange';
import moment from 'moment'
import momentTimezone from 'moment-timezone';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    title: {
        marginLeft: 14,
        width: "900px",
        paddingTop: '4px'
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
        paddingTop: '4px'
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    loadingScreen: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
});

const ClientVersionsCheckList = props => {
    const { classes, translations, activeCustomerID, tableRef, saveColumnsFilter, columns, onFetchSupportedClientTypeUsage, onFetchSupportedClientTypes } = props;
    const [columnObject, setColumnObject] = useState([]);
    const [supportedClientTypes, setSupportedClientTypes] = useState([]);

    const [dateRangeValue, setDateRangeValue] = useState(null);

    const visible = columns && columns['ClientVersionsCheck'];

    const tableOptions = {
        filtering: false,
        sorting: false,
        selection: false,
        search: false,
        minBodyHeight: window.innerHeight - 205 < 300 ? 300 : window.innerHeight - 205,
        maxBodyHeight: window.innerHeight - 205,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        paging: false
    };

    const getData = async () => {
        const params = {
            ActiveCustomerID: activeCustomerID,
            ClientTypes: supportedClientTypes,
            From: dateRangeValue?.From.format('YYYY-MM-DD'),
            To: dateRangeValue?.To.format('YYYY-MM-DD'),
        }
        return await onFetchSupportedClientTypeUsage(params);
    }

    useEffect(() => {
        const getSupportedClientTypes = async () => {
            const result = await onFetchSupportedClientTypes({ ActiveCustomerID: activeCustomerID });
            if (result) {
                setSupportedClientTypes(Object.keys(result));
            }
        }
        getSupportedClientTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const getClientTypeUsage = async () => {
            const result = await getData();
            if (result && result.length > 0) {
                const columns = Object.keys(result[0]).map((tableColumn) => {
                    return {
                        title: tableColumn,
                        field: tableColumn,
                        hidden: visible ? visible[tableColumn] : false,
                        render: rowData => {
                            return <Typography variant={'caption'}>{rowData[tableColumn] || translations['short_not_assigned'] || 'n/a'}</Typography>;
                        }
                    }
                })
                setColumnObject(columns);
            }
        }
        getClientTypeUsage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportedClientTypes])

    useEffect(() => {
        const customerTimezone = window.localStorage.getItem("CustomerTimezone") ?? 'UTC';

        const from = momentTimezone.tz(moment().subtract(7, 'days'), customerTimezone);

        const to = momentTimezone.tz(moment(), customerTimezone);
        
        setDateRangeValue({
            From: from,
            To: to,
            maxDate: to.set({
                hours: 23,
                minutes: 59,
                seconds: 59,
            })
        });
        // eslint-disable-next-line
    }, [ activeCustomerID ]);

    const filterDataRange = async (range) => {
        setDateRangeValue({ From: range.From, To: range.To });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const tableTitle = () => {
        return (
            <div className={classes.title}>
                <div className={classes.tableTitle}>
                    {BackButton(translations, classes.customTooltip)}
                    <Typography className={classes.typography}>{translations['client_type_usage'] || 'Client type usage'}</Typography>
                    <Tooltip title={translations["refresh"] || "Refresh"}>
                        <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </div>
                <div className={classes.filters}>
                    {dateRangeValue && <DataRange
                        initialValues={dateRangeValue}
                        filterDataRange={filterDataRange}
                        value={dateRangeValue}
                        setValue={setDateRangeValue}
                        format="yyyy/MM/dd 00:00:00"
                        translations={translations}
                    />}
                </div>
            </div>
        )
    }

    const columnHidden = newColumnState => {
        const objToSendColumn = {}
        const objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`ClientVersionsCheck`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    async function fetchTableData() {
        const result = await getData();
        return { data: result, totalCount: result.length };
    }

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                localization={localization(translations)}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        saveColumnsFilter: (data) => dispatch(action.saveColumnsFilter(data)),
        onFetchSupportedClientTypeUsage: (data) => dispatch(fetchSupportedClientTypeUsage(data)),
        onFetchSupportedClientTypes: (data) => dispatch(fetchClientTypes(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ClientVersionsCheckList));